import { GoogleAnalyticsConfig } from "@/tracking/adapters/GoogleAnalyticsAdapter";

import { PixelConfig } from "@/tracking/adapters/BaseTrackingAdapter";
import { resolveCdpVisitor } from "@/lib/cdpVisitor";

export interface OrderedList<T> {
    [index: number]: T;
}

export interface OrganisationLegalInformation {
    addressLine: string;
    chamberOfCommerceNumber: string;
    companyName: string;
    contactEmail: string;
    contactPhone: string;
    termsAndConditionsUrl: string;
    vatNumber: string;
    showInFooter?: boolean;
    showOnlyMinimalInformation?: boolean;
}

export interface Shop {
    slug: string,
    language: string,
    languages: string[],
    style: ShopStyle,
    events: Event[],
    defaultCurrency: string,
    paymentMethods: PaymentMethod[],
    organisationLegalInformation: OrganisationLegalInformation,
    conversionTracking: {
        trackers: (PixelConfig|GoogleAnalyticsConfig)[],
    },
    queueId?: string,
    queueEndpoint?: string,
    queueVideo?: {
        url: string,
        duration: number,
    },
    defaultLanguage?: string,
    supportedLanguages?: { code: string; name: string; flagIcon: string }[],
    lockedShopConfiguration?: LockedShopConfiguration,
    // form: OrderedList<FormComponent>,
}

export interface PaymentMethod {
    id: string;
    fee: ServiceFee;
    name: string;
    image: string;
    // Extra customer fields this method needs at checkout (e.g. BNPL: billingAddress, phone,
    // birthDate). Render inputs for these when the method is selected and send them in the
    // checkout call's `customer` object.
    requiredCustomerFields?: string[];
    // Order amount window (minor units) enforced by the method, or null when unbounded.
    // Hide the method when the order total (incl. its payment fee) falls outside the window.
    minOrderAmount?: number | null;
    maxOrderAmount?: number | null;
}

export interface CustomerBillingAddress {
    streetName: string;
    streetNumber: string;
    zipCode: string;
    city: string;
    countryCode: string; // ISO 3166-1 alpha-2, e.g. "NL"
}

// Method-specific customer fields (see PaymentMethod.requiredCustomerFields).
export interface CustomerExtraInformation {
    phone?: string;
    birthDate?: string; // YYYY-MM-DD
    billingAddress?: CustomerBillingAddress;
}

export interface FormComponent {
    key: string;
    type: string;
}

export interface Event {
    eventId: string;
    slug: string;
    name: string;
    headerImage?: string;
    description?: string;
    location: {
        name: string;
    },
    startDate: string;
    endDate: string;
    productPages: ProductPage[];
}

export interface ProductPage {
    name: string;
    slug: string;
    icon: IconName;
    productSections: ProductSection[];
    canAdvanceToNextPageWithoutSelectingProduct: boolean;
}

export interface ProductSection {
    id: string;
    name?: string;
    description?: string;
    openedDefault: boolean;
    canCollapse: boolean;
    products: Product[];
}

export interface Media<T> {
    type: string;
    img: T;
}

export type Icon = Media<string>;
export type Image = Media<string>;
export type Carousel = Media<string[]>;

export enum IconName {
    Ticket = "ticket",
    CreditCard = "credit-card",
    PlusFilled = "plus-filled",
    Bed = "bed",
}

export interface NotListed {
    type: "notListed";
}

export interface NotAvailable {
    type: "notAvailable";
}

export interface Available {
    type: "available";
}

export interface AvailableBetween {
    type: "available_between";
    availableFrom: string | null;
    availableUntil: string | null;
    showStartDateTime: boolean;
    showSoldOutOnEndDateTime: boolean;
}

export type Availability = NotListed | NotAvailable | Available | AvailableBetween;

// Represents a sub-product within a GroupedProduct
export interface GroupedProductItem {
    productId: string;
    name: string;
    description?: string | null;
    price: MoneyValues;
    serviceFees?: { items: ServiceFee[] };
    /** @deprecated Use serviceFees instead */
    serviceFee?: ServiceFee | null;
    quantity: number;
}

/**
 * FAS-1053 — an entrance time slot. `startsAt`/`endsAt` are ISO-8601 with the event zone's
 * offset, so they must be rendered in that offset rather than the browser's (see `@/lib/timeSlot`).
 */
export interface TimeSlot {
    id: string;
    startsAt: string;
    endsAt: string;
}

export interface ProductTimeSlot extends TimeSlot {
    /** The organiser took this slot out of sale. */
    closed: boolean;
    /** Same wire values as a product's own inventoryStatus. */
    inventoryStatus: "available"|"all_reserved"|"sold_out";
}

// Base interface with common fields for all product types
interface BaseProduct {
    productId: string;
    name: string;
    description?: string;
    photo: Icon|Image|Carousel;
    price: MoneyValues;
    serviceFees?: { items: ServiceFee[] };
    /** @deprecated Use serviceFees instead */
    serviceFee?: ServiceFee;
    inventoryStatus: "available"|"all_reserved"|"sold_out";
    availability: Availability;
    statusMessage?: string;
    // startSale and endSale might not apply directly to grouped products,
    // but included for consistency as they are in the example JSON.
    // Consider if they should be optional or handled differently.
    startSale?: Date; // Make optional or review if needed for grouped products
    endSale?: Date; // Make optional or review if needed for grouped products
    maxQuantity: number;
    minQuantity: number;
    stepQuantity: number;
    isHidden: boolean;
    /**
     * FAS-1053 — non-empty when the product is sold PER SLOT: the shop then renders one row per
     * slot with its own quantity instead of a single stepper, and every cart line of this product
     * carries a `timeSlotId`. Absent or empty for every other product.
     */
    timeSlots?: ProductTimeSlot[];
    /**
     * FL-581 — tiered ticketing. Present only on a ticket that goes on sale after another one:
     * that ticket's id, its inventory status, when its own sale ends, and whether it is gone
     * (archived / not for sale). See `waitsForPreviousTier`.
     */
    availableAfterProductId?: string | null;
    availableAfterInventoryStatus?: "available"|"all_reserved"|"sold_out" | null;
    availableAfterSaleEndsAt?: string | null;
    availableAfterClosed?: boolean | null;
}

// Interface for a standard product
export interface StandardProduct extends BaseProduct {
    type?: 'standard'; // Optional or default type for standard products
}

// Interface for a grouped product
export interface GroupedProduct extends BaseProduct {
    type: 'grouped_product';
    products: GroupedProductItem[];
    /** FAS-2031 — what the organiser takes off the bundle. The api sends the amount negative. */
    discountLines?: GroupedProductDiscountLine[];
}

export interface GroupedProductDiscountLine {
    name: string;
    amount: { amount: number; currency: string };
}

// Donation product types
export interface DonationOption {
    amount: number;
    currency: string;
}

export interface DonationCustomLimit {
    lowerLimit: {
        amount: number;
        currency: string;
    };
    upperLimit: {
        amount: number;
        currency: string;
    };
}

export interface DonationDetails {
    options: DonationOption[];
    customDonation?: DonationCustomLimit;
}

export interface DonationProduct extends BaseProduct {
    type: 'donation';
    donation: DonationDetails;
}

export interface CashcardOption {
    amount: number;
    currency: string;
}

export interface CashcardCustomLimit {
    lowerLimit: {
        amount: number;
        currency: string;
    };
    upperLimit: {
        amount: number;
        currency: string;
    };
}

export interface CashcardDetails {
    options: CashcardOption[];
    customTopup?: CashcardCustomLimit;
}

export interface CashcardProduct extends BaseProduct {
    type: 'cashcard';
    cashcard: CashcardDetails;
}

export interface CashlessWalletOption {
    amount: number;
    currency: string;
}

export interface CashlessWalletCustomTopup {
    lowerLimit: {
        amount: number;
        currency: string;
    };
    upperLimit: {
        amount: number;
        currency: string;
    };
}

export interface CashlessWalletDetails {
    walletGroupId: string;
    options: CashlessWalletOption[];
    customTopup?: CashlessWalletCustomTopup;
    priceOverridePercentage?: number;
}

export interface CashlessWalletProduct extends BaseProduct {
    type: 'cashlessWallet';
    cashlessWallet: CashlessWalletDetails;
}

// Union type representing either a standard, grouped, donation, cashcard, or cashless wallet product
export type Product = StandardProduct | GroupedProduct | DonationProduct | CashcardProduct | CashlessWalletProduct;

export interface MoneyValues {
    amount: number;
    currency: string;
}

export interface ServiceFee {
    name: string;
    amountPerUnit?: MoneyValues;
    percentage?: number;
}

export interface ShopStyle {
    panelBackgroundColor?: string;
    textColor?: string;
    removeFromCartButtonColor?: string;
    removeFromCartButtonTextColor?: string;
    addToCartButtonColor?: string;
    addToCartButtonTextColor?: string;
    productErrorMessageBackground?: string;
    productErrorMessageText?: string;
    ctaButtonColor?: string;
    ctaButtonTextColor?: string;
    borderColor?: string;
    pageBackgroundFromColor?: string;
    pageBackgroundToColor?: string;
    pageBackgroundImage?: string;
    hideLocation?: boolean;
    hideDate?: boolean;
    hideCoupon?: boolean;
}

/** Free-form per-line bag: the chosen amount for a donation/wallet, the slot for a slot product. */
export interface ProductSettings {
    amount?: number;
    currency?: string;
    /** FAS-1053 — required on a line of a product that has time slots. */
    timeSlotId?: string;
}

export interface ProductQuantity {
    /**
     * FAS-1053 — ulid minted by the shop, stable for the lifetime of the cart line. Optional on
     * the wire (the API falls back to the product id so legacy carts keep working) but always sent
     * by this client: it is what makes two lines of the same product possible.
     */
    lineId?: string;
    productId: string;
    quantity: number;
    settings?: ProductSettings;
    /**
     * FAS-1622 — what this line actually costs per unit, coupon discount already applied.
     * Only present on a cart the API has priced (never on a request we send it); absent on an
     * older API response, in which case the shop falls back to pricing the line itself.
     */
    unitPriceInCents?: number;
    /** The per-unit price before any coupon discount — equal to `unitPriceInCents` when nothing is discounted. */
    originalUnitPriceInCents?: number;
    /** `0` when this line has no discount. */
    discountPerUnitInCents?: number;
    currency?: string;
}

export enum PaymentStatus {
    Pending = 'pending',
    Authorized = 'authorized',
    Paid = 'paid',
    Cancelled = 'cancelled',
    Expired = 'expired',
    Declined = 'declined'
}

export interface Payment {
    status: PaymentStatus;
    methodId: string;
    startedAt: string;
}

export interface Cart {
    cartId: string;
    shopSlug: string;
    products: ProductQuantity[];
    timedOut: boolean;
    form: OrderedList<FormComponent>;
    answers: object;
    errors: string[]|string[][];
    payments: {[paymentId: string]: Payment;}
    isCheckedOut: boolean;
    pendingPayment: string|null;
    orderId: string|null;
    appliedCouponCode: Coupon|null;
    checkoutAtEventId: string|null;
    /** FAS-1622 — null when no coupon discount applies to this cart. */
    discount: CartDiscount|null;
}

export interface Coupon {
    code: string;
    unlockProducts: null|UnlockProducts;
    shopUnlock?: ShopUnlock;
    /** FAS-1622 — false for an unlock-only code that carries no price discount. */
    hasDiscount: boolean;
    /**
     * FAS-2016 — how often this code may be used in total: a number, or null for unlimited.
     * Absent on an older API, which only ever had single-use shop-unlock codes.
     */
    maxUses?: number | null;
}

/** FAS-1622 — the coupon discount applied to the cart as a whole. */
export interface CartDiscount {
    code: string|null;
    totalInCents: number;
    currency: string;
    appliesToServiceFee: boolean;
}

export interface LockedShopConfiguration {
    require_coupon_code: boolean;
}

export interface ShopUnlock {
    slug: {
        slug: string;
    };
    maxProducts: number | null;
    products_in_max_products: string[] | null;
    maxQuantityPerProduct: { productId: string; quantity: number }[] | null;
}

export interface UnlockProducts {
    products: ProductUnlock[];
    maxProducts?: number | null;
}

export interface ProductUnlock {
    productId: string;
    quantity: number;
}

export interface Order {
    orderId: string;
    shopStyle: ShopStyle;
    tickets: Ticket[];
    contactDetails: {
        firstName: string;
        lastName: string;
        email: string;
    };
}

interface BaseTicket {
    ticketId: string;
    productId: string;
    productName: string;
    productPrice: MoneyValues;
}

export interface AvailableTicket extends BaseTicket {
    ticketStatus: TicketStatus.available;
    canBeAddedToWallet: boolean;
    canBeDownloadedAsPdf: boolean;
    barcode: string;
}

export interface SealedTicket extends BaseTicket {
    ticketStatus: TicketStatus.sealed;
    availableAt: string;
    // scans: Scan[];
}

export interface ClaimableTicket extends BaseTicket {
    ticketStatus: TicketStatus.claimable;
    claimForm: OrderedList<FormComponent>;
    canClaimSelf: boolean;
}

export interface ClaimedTicket extends BaseTicket {
    ticketStatus: TicketStatus.claimed;
    claimedAt: string;
    claimedBy: string;
}

export type Ticket = AvailableTicket | ClaimableTicket | ClaimedTicket | SealedTicket;

export enum TicketStatus {
    available = "available",
    sealed = "sealed",
    claimed = "claimed",
    claimable = "claimable",
}

export interface PaymentResponse {
    type: "redirect"|"embed"|"embedded_payment"|"no_payment_required"|"extra_information_needed"|"error";
    url?: string;
    error?: string;
    // For type=extra_information_needed: the selected method requires customer fields that are
    // missing or invalid. Re-submit the same checkout with the `customer` object completed —
    // safe, the rejected attempt persisted nothing.
    paymentMethodId?: string;
    missingFields?: string[];
    requiredCustomerFields?: string[];
    // For type=embedded_payment (PAY.PARTS): the buyer pays without leaving the shop. The API
    // starts the session only after a method is chosen, so the session amount already includes
    // that method's fee — which is why exactly one component is named here rather than a picker.
    sessionToken?: string;
    component?: string;
    sdkUrl?: string;
    apiUrl?: string;
    returnUrl?: string;
    // Used to report the completed payment back (see reportEmbeddedPaymentResult). The token binds
    // this payment to this cart, so it is only good for the checkout that issued it.
    paymentId?: string;
    resultToken?: string;
}

export function findElementInOrderedList<T>(orderedList: { [index: number]: T }, predicate: (item: T) => boolean): T | undefined {
    const keys = Object.keys(orderedList).map(key => parseInt(key, 10));
    const key = keys.find(key => predicate(orderedList[key]));
    return key !== undefined ? orderedList[key] : undefined;
}

export function getFirstElement<T>(orderedList: { [index: number]: T }): T | undefined {
    const sortedKeys = Object.keys(orderedList).map(key => parseInt(key, 10)).sort((a, b) => a - b);
    const firstKey = sortedKeys[0];
    return orderedList[firstKey];
}

export function getElementAfter<T>(orderedList: { [index: number]: T }, predicate: T): T | undefined {
    const keys = Object.keys(orderedList).map(key => parseInt(key, 10));
    const sortedKeys = keys.sort((a, b) => a - b);

    const index = sortedKeys.findIndex(key => orderedList[key] === predicate);
    if (index === -1) {
        return undefined;
    }
    const nextKey = sortedKeys[index + 1];
    return nextKey !== undefined ? orderedList[nextKey] : undefined;
}

export function getProduct(shop: Shop, productId: string): Product | undefined {
    for (const eventKey of Object.keys(shop.events)) {
        const event = shop.events[parseInt(eventKey, 10)];
        for (const productPageKey of Object.keys(event.productPages)) {
            const productPage = event.productPages[parseInt(productPageKey, 10)];
            for (const productSectionKey of Object.keys(productPage.productSections)) {
                const productSection = productPage.productSections[parseInt(productSectionKey, 10)];
                const product = findElementInOrderedList(productSection.products, product => product.productId === productId);
                if (product !== undefined) {
                    return product;
                }
            }
        }
    }
    return undefined;
}

export interface ReserveProductRejection {
    error: string;
    error_code: string;
    product_id?: string;
    message?: string;
}

export function isReserveProductRejection(value: unknown): value is ReserveProductRejection {
    return typeof value === 'object' && value !== null && 'error_code' in value && 'error' in value;
}

export enum ReserveProductError {
    NotForSale = "NotForSale",
    SoldOut = "SoldOut",
    NotEnoughInStock = "NotEnoughInStock",
    NotAvailable = "NotAvailable",
    NotInCart = "NotInCart",
    CartTimeout = "CartTimeout",
}

export class TicketApi {
    private readonly ATTRIBUTION_KEY = 'dGhpc0lzQTMyQnl0ZUtleUZvckF0dHJpYnV0aW9uRGF0YQ=='; // 32-byte key in base64

    constructor(
        private readonly baseUrl: string,
        private readonly shopSlug: string,
    ) {
    }

    private getHeaders(additionalHeaders: Record<string, string> = {}) {
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            ...this.getBaseHeaders(),
            ...additionalHeaders
        };

        return headers;
    }

    private getBaseHeaders(additionalHeaders: Record<string, string> = {}) {
        const headers: Record<string, string> = {
            ...additionalHeaders
        };

        // Add queue session ID if available
        const sessionId = localStorage.getItem('queueSessionId');
        if (sessionId) {
            headers['X-QUEUE-GUEST-ID'] = sessionId;
        }

        return headers;
    }

    private hashAttribution(data: object): string {
        // Convert to base64 first to handle special characters
        const jsonString = JSON.stringify(data);
        const base64 = btoa(jsonString);
        
        // Decode the base64 key to get the actual bytes
        const keyBytes = atob(this.ATTRIBUTION_KEY);
        
        // XOR with the full key
        let result = '';
        for(let i = 0; i < base64.length; i++) {
            result += String.fromCharCode(base64.charCodeAt(i) ^ keyBytes.charCodeAt(i % keyBytes.length));
        }
        return btoa(result); // Convert back to base64 for safe transmission
    }

    private collectAttributionData(): object {
        const queryParams = Object.fromEntries(new URLSearchParams(window.location.search));
        const { visitorId, linkedAnonId } = resolveCdpVisitor();
        // The shop's persistent first-party visitor id — keys this cart's touchpoint to one visitor
        // across sessions. Overrides any forwarded ?cdp_anon_id in the URL; a *different* forwarded id
        // is passed as cdp_prev_anon_id so the backend merges the two anon journeys into one profile.
        queryParams.cdp_anon_id = visitorId;
        return {
            queryParams,
            ...(linkedAnonId ? { cdp_prev_anon_id: linkedAnonId } : {}),
            client: {
                userAgent: navigator.userAgent,
                url: window.location.href,
                referrer: document.referrer,
            },
            fb: {
                fbp: document.cookie.match('_fbp=([^;]*)') ? document.cookie.match('_fbp=([^;]*)')?.[1] ?? null : null,
                fbc: (() => {
                    // First try to get from cookie
                    const fromCookie = document.cookie.match('_fbc=([^;]*)') ? document.cookie.match('_fbc=([^;]*)')?.[1] ?? null : null;
                    if (fromCookie) return fromCookie;
                    
                    // If not in cookie, check URL parameter
                    const urlParams = new URLSearchParams(window.location.search);
                    const fbclid = urlParams.get('fbclid');
                    if (fbclid) {
                        return `fb.1.${Date.now()}.${fbclid}`;
                    }
                    return null;
                })()
            },
            tiktok: {
                ttp: document.cookie.match('_ttp=([^;]*)') ? document.cookie.match('_ttp=([^;]*)')?.[1] ?? null : null,
                ttclid: (() => {
                    // First check URL parameter
                    const urlParams = new URLSearchParams(window.location.search);
                    const ttclidParam = urlParams.get('ttclid');
                    if (ttclidParam) return ttclidParam;
                    
                    // If not in URL, check cookie
                    const fromCookie = document.cookie.match('ttclid=([^;]*)') ? document.cookie.match('ttclid=([^;]*)')?.[1] ?? null : null;
                    return fromCookie || null;
                })()
            }
        };
    }

    public async loadCart(cartId: string, language?: string): Promise<Cart> {
        const url = new URL(`${this.baseUrl}/shop/${this.shopSlug}/cart/${cartId}`);
        if (language) {
            url.searchParams.set('language', language);
        }
        
        const res = await fetch(url.toString(), {
            cache: "no-cache",
            headers: this.getBaseHeaders({
                "X-Meta-Original-Checkout-Event": cartId
            })
        });
        if (!res.ok) {
            if (res.status === 404) {
                throw new Error("Cart not found");
            }
            throw new Error(`Failed to load cart: ${res.status}`);
        }
        return await res.json();
    }

    public async applyCoupon(cartId: string, couponCode: string): Promise<Cart|string> {
        const res = await fetch(`${this.baseUrl}/shop/${this.shopSlug}/cart/${cartId}/apply-coupon`, {
            method: "POST",
            headers: this.getHeaders(),
            body: JSON.stringify({
                code: couponCode,
            }),
        });

        if(res.status === 422){
            const body = await res.json();
            return body.message;
        }

        return await res.json();
    }

    public async reserveProducts(cartId: string, products: ProductQuantity[], trackingEventId?: string, language?: string): Promise<Cart|ReserveProductError|ReserveProductRejection> {
        const url = new URL(`${this.baseUrl}/shop/${this.shopSlug}/cart/${cartId}/reserve-tickets`);
        if (language) {
            url.searchParams.set('language', language);
        }
        
        const res = await fetch(url.toString(), {
            method: "POST",
            headers: this.getHeaders(),
            body: JSON.stringify({
                products: products,
                trackingEventId: trackingEventId,
                attribution: this.hashAttribution(this.collectAttributionData())
            }),
        });

        if(res.status === 400){
            return ReserveProductError.CartTimeout;
        }

        // The API rejects a product it can't reserve (unavailable, invalid product settings, ...)
        // with a 422 and a per-product reason. Without this the body was cast to a Cart and fed
        // into updateCart, wiping the cart state and hiding the reason from the customer.
        if (res.status === 422) {
            return await res.json() as ReserveProductRejection;
        }

        if (res.status === 500) {
            throw new Error("Cart not found");
        }

        if (res.status === 404) {
            throw new Error("Cart not found");
        }

        return await res.json();
    }

    public async loadShop(language: string, unlockedProductIds: string[]): Promise<Shop> {
        // build query params
        const queryParams = new URLSearchParams();
        queryParams.set("language", language);
        if(unlockedProductIds.length > 0) {
            queryParams.set("unlocked_products", unlockedProductIds.join(","));
        }

        const res = await fetch(`${this.baseUrl}/shop/${this.shopSlug}?${queryParams.toString()}`, {
            cache: "no-cache",
            headers: this.getBaseHeaders()
        });
        if (!res.ok) {
            throw new Error(`Failed to load shop: ${res.status}`);
        }
        return await res.json();
    }

    public async setFormAnswers(cartId: string, answers: object, language?: string): Promise<Cart> {
        const url = new URL(`${this.baseUrl}/shop/${this.shopSlug}/cart/${cartId}/submit-form`);
        if (language) {
            url.searchParams.set('language', language);
        }
        
        const res = await fetch(url.toString(), {
            method: "POST",
            headers: this.getHeaders(),
            body: JSON.stringify(answers),
        });
        return await res.json();
    }

    /**
     * Tells the API that an embedded (PAY.PARTS) payment completed, passing the pay.nl order id the
     * component reported. A fallback for a lost exchange: an embedded payment has no order id until
     * this point, so without it the backend has nothing to poll.
     *
     * The order id is only a hint — the API verifies it against pay.nl before believing it — so a
     * failure here is not worth surfacing to the buyer.
     */
    public async reportEmbeddedPaymentResult({cartId, paymentId, orderId, token}: {cartId: string, paymentId: string, orderId: string, token: string}): Promise<void> {
        await fetch(`${this.baseUrl}/shop/${this.shopSlug}/cart/${cartId}/payment-result`, {
            method: "POST",
            headers: this.getHeaders(),
            body: JSON.stringify({paymentId, orderId, token}),
            keepalive: true,
        });
    }

    public async checkout({cartId, eventId, paymentMethodId, redirectUrl = null, trackingEventId, languageCode, customer = null, preferRedirect = false}: {cartId: string, eventId: string, paymentMethodId: string|null, redirectUrl: string|null, trackingEventId: string|null, languageCode?: string, customer?: CustomerExtraInformation|null, preferRedirect?: boolean}): Promise<PaymentResponse> {
        const currentUrlWithoutHash = window.location.origin + window.location.pathname;

        const res = await fetch(`${this.baseUrl}/shop/${this.shopSlug}/cart/${cartId}/checkout`, {
            method: "POST",
            headers: this.getHeaders(),
            body: JSON.stringify({
                eventId: eventId,
                // Set when an embedded component failed to mount: ask for the hosted flow so the
                // buyer isn't left on a form that will never work.
                preferRedirect: preferRedirect,
                redirect: redirectUrl ?? currentUrlWithoutHash,
                paymentMethodId: paymentMethodId,
                trackingEventId: trackingEventId,
                attribution: this.hashAttribution(this.collectAttributionData()),
                languageCode: languageCode,
                customer: customer ?? undefined,
            }),
        });

        if (res.status === 422) {
            const body = await res.json();
            if (body === "coupon_error" || (typeof body === 'object' && body !== null && body.type === "coupon_error")) {
                throw new Error(JSON.stringify({
                    type: "coupon_error",
                    message: body.message ?? "Coupon code limit reached, all products are already sold or reserved"
                }));
            }
            throw new Error(`Checkout failed with status 422: ${JSON.stringify(body)}`);
        }

        // Any other refusal (e.g. 409 cart_already_checked_out, or the 500 older APIs answer with)
        // is an error, not a PaymentResponse — parsing it as one left the pay button silently dead.
        if (!res.ok) {
            throw new Error(`Checkout failed with status ${res.status}: ${await res.text()}`);
        }

        return await res.json();
    }

    public async forceCancelPendingPayment(cartId: string, paymentId: string): Promise<void> {
        await fetch(`${this.baseUrl}/shop/${this.shopSlug}/cart/${cartId}/cancel-pending-payment`, {
            method: "POST",
            headers: this.getHeaders(),
            body: JSON.stringify({
                paymentId: paymentId,
            }),
        });    
    }

    public async leaveCart(cartId: string): Promise<void> {
        const url = `${this.baseUrl}/shop/${this.shopSlug}/cart/${cartId}/leave-cart`;
        
        try {
            // Use sendBeacon for better reliability during page unload
            if (navigator.sendBeacon) {
                // Try POST with sendBeacon first (most reliable)
                const success = navigator.sendBeacon(url, new Blob(['{}'], { type: 'application/json' }));
                if (success) {
                    return;
                }
                
                // If POST fails, try GET with sendBeacon (simpler, no body)
                const getSuccess = navigator.sendBeacon(url + '?method=get');
                if (getSuccess) {
                    return;
                }
            }
            
            // Fallback to fetch with keepalive
            await fetch(url, {
                method: "POST",
                headers: this.getHeaders(),
                body: JSON.stringify({}),
                keepalive: true
            });
        } catch (error) {
            // Silently fail - this is a cleanup operation that shouldn't block the user
            console.warn('Failed to call leave-cart endpoint:', error);
        }
    }

    public async getOrder(orderId: string): Promise<Order> {
        const res = await fetch(`${this.baseUrl}/orders/${orderId}`, {
            cache: "no-cache",
            headers: this.getBaseHeaders()
        });
        if (!res.ok) {
            throw new Error(`Failed to load order: ${res.status}`);
        }
        return await res.json();
    }
}
